import type { RailItem } from "./rail.types"
import { Check, Pencil, X, MessageCircle, ArrowRight } from "lucide-react"

const MAX_RAIL_ITEMS = 5

const PRIORITY_ORDER: Record<RailItem["type"], number> = {
  goal_anchor: 0,
  memory_suggestion: 1,
  advisor_question: 2,
  next_prompt: 3,
  decision_review: 4,
  confirmation: 5,
}

export const STATIC_RAIL_ITEMS: RailItem[] = [
  // 1. Goal Anchor
  {
    id: "goal-anchor-1",
    type: "goal_anchor",
    severity: "neutral",
    titleLabel: "90-DAY GOAL",
    primaryText: "Close 3 new enterprise clients and establish repeatable outbound process",
    actions: [
      {
        id: "view-goal",
        label: "View in Goals",
        kind: "primary",
        icon: ArrowRight,
        intent: "navigate",
        href: "/goals",
      },
    ],
  },

  // 2. Memory Suggestion
  {
    id: "memory-suggestion-1",
    type: "memory_suggestion",
    severity: "attention",
    titleLabel: "MEMORY SUGGESTION",
    primaryText: "Current monthly revenue is approximately $45K MRR from 12 active clients",
    thinkingContext: "From your message about pricing strategy in the conversation about Q1 pricing review",
    actions: [
      {
        id: "approve-memory",
        label: "Approve",
        kind: "primary",
        icon: Check,
        intent: "approve",
      },
      {
        id: "edit-memory",
        label: "Edit",
        kind: "secondary",
        icon: Pencil,
        intent: "edit",
      },
      {
        id: "dismiss-memory",
        label: "Dismiss",
        kind: "tertiary",
        icon: X,
        intent: "dismiss",
      },
    ],
  },

  // 3. Advisor Question
  {
    id: "advisor-question-1",
    type: "advisor_question",
    severity: "caution",
    titleLabel: "ADVISOR QUESTION",
    primaryText: "What's your average deal size for new enterprise clients?",
    thinkingContext:
      "This helps me assess whether your 3-client goal aligns with your revenue targets, based on your earlier pricing discussion in Q1.",
    actions: [
      {
        id: "answer-question",
        label: "Answer in chat",
        kind: "primary",
        icon: MessageCircle,
        intent: "inject_prompt",
        payload: { prompt: "My average enterprise deal size is..." },
      },
      {
        id: "skip-question",
        label: "Dismiss",
        kind: "secondary",
        icon: X,
        intent: "dismiss",
      },
    ],
  },

  // 4. Next Best Prompt
  {
    id: "next-prompt-1",
    type: "next_prompt",
    severity: "attention",
    titleLabel: "SUGGESTED NEXT STEP",
    primaryText: "Review my 90-day goal and recommend specific weekly milestones",
    thinkingContext: "Based on your goal to close 3 enterprise clients in Q1 and your current outbound strategy",
    actions: [
      {
        id: "use-prompt",
        label: "Ask this",
        kind: "primary",
        icon: MessageCircle,
        intent: "inject_prompt",
        payload: {
          prompt: "Review my 90-day goal and recommend specific weekly milestones to achieve it",
        },
      },
      {
        id: "dismiss-prompt-1",
        label: "Dismiss",
        kind: "secondary",
        icon: X,
        intent: "dismiss",
      },
    ],
  },

  // 5. Confirmation (kept for reference, but filtered out in display)
  {
    id: "confirmation-1",
    type: "confirmation",
    severity: "success",
    primaryText: 'Saved: "Focus on outbound LinkedIn strategy for Q1"',
  },

  // === TEMPORARY TEST CARDS FOR SCROLL VALIDATION ===
  // These cards are UI-only test data and should be removed after scroll validation is complete

  // +1 Memory Suggestion (TEST)
  {
    id: "memory-suggestion-test-2",
    type: "memory_suggestion",
    severity: "attention",
    titleLabel: "MEMORY SUGGESTION",
    primaryText: "Average enterprise deal size is $15K annually with 6-month sales cycles",
    thinkingContext: "Extracted from your discussion about Q1 revenue projections and sales pipeline velocity",
    actions: [
      {
        id: "approve-memory-2",
        label: "Approve",
        kind: "primary",
        icon: Check,
        intent: "approve",
      },
      {
        id: "edit-memory-2",
        label: "Edit",
        kind: "secondary",
        icon: Pencil,
        intent: "edit",
      },
      {
        id: "dismiss-memory-2",
        label: "Dismiss",
        kind: "tertiary",
        icon: X,
        intent: "dismiss",
      },
    ],
  },

  // +1 Advisor Question (TEST)
  {
    id: "advisor-question-test-2",
    type: "advisor_question",
    severity: "caution",
    titleLabel: "ADVISOR QUESTION",
    primaryText: "How many qualified leads are currently in your pipeline for Q1?",
    thinkingContext:
      "Knowing your current pipeline helps me assess whether your 3-client goal is realistic given your 6-month sales cycle mentioned earlier.",
    actions: [
      {
        id: "answer-question-2",
        label: "Answer in chat",
        kind: "primary",
        icon: MessageCircle,
        intent: "inject_prompt",
        payload: { prompt: "I currently have X qualified enterprise leads in my Q1 pipeline..." },
      },
      {
        id: "skip-question-2",
        label: "Dismiss",
        kind: "secondary",
        icon: X,
        intent: "dismiss",
      },
    ],
  },

  // +1 Next Prompt (TEST)
  {
    id: "next-prompt-test-2",
    type: "next_prompt",
    severity: "attention",
    titleLabel: "SUGGESTED NEXT STEP",
    primaryText: "Break down my enterprise sales process into repeatable weekly actions",
    thinkingContext: "Based on your goal to establish a repeatable outbound process and your $15K deal targets",
    actions: [
      {
        id: "use-prompt-2",
        label: "Ask this",
        kind: "primary",
        icon: MessageCircle,
        intent: "inject_prompt",
        payload: {
          prompt: "Help me break down my enterprise sales process into repeatable weekly actions",
        },
      },
      {
        id: "dismiss-prompt-2",
        label: "Dismiss",
        kind: "secondary",
        icon: X,
        intent: "dismiss",
      },
    ],
  },

  // +1 Next Prompt (TEST)
  {
    id: "next-prompt-test-3",
    type: "next_prompt",
    severity: "attention",
    titleLabel: "SUGGESTED NEXT STEP",
    primaryText: "Create a LinkedIn outreach template for enterprise prospects",
    thinkingContext:
      "Your focus on outbound LinkedIn strategy and need for repeatable processes suggests templated messaging could accelerate your pipeline",
    actions: [
      {
        id: "use-prompt-3",
        label: "Ask this",
        kind: "primary",
        icon: MessageCircle,
        intent: "inject_prompt",
        payload: {
          prompt: "Help me create an effective LinkedIn outreach template for enterprise prospects",
        },
      },
      {
        id: "dismiss-prompt-3",
        label: "Dismiss",
        kind: "secondary",
        icon: X,
        intent: "dismiss",
      },
    ],
  },
]

export function buildRailItemsFromSession(_args?: {
  userId?: string
  tenantId?: string
  conversationId?: string
  goals?: any[]
  memoryProposals?: any[]
  contextGaps?: any[]
  nextPrompts?: any[]
  decisions?: any[]
}): RailItem[] {
  const items = [...STATIC_RAIL_ITEMS]

  const goalAnchor = items.find((i) => i.type === "goal_anchor")

  const actionableItems = items
    .filter((i) => i.type !== "goal_anchor" && i.type !== "confirmation")
    .sort((a, b) => PRIORITY_ORDER[a.type] - PRIORITY_ORDER[b.type])

  const confirmations = items.filter((i) => i.type === "confirmation")

  return [goalAnchor, ...actionableItems, ...confirmations].filter(Boolean) as RailItem[]
}
